import { useEffect, useRef, useState } from "react"
import { fetchDeliveryQuote, type DeliveryQuote } from "@/lib/delivery-quote/fetch-delivery-quote"
import type { Address } from "@/types"

/** Per-address quote state: still loading, answered by the core, or unavailable (fall back to the local preview). */
export type AddressQuote =
  | { status: "loading" }
  | { status: "ready"; quote: DeliveryQuote }
  | { status: "unavailable" }

const addressKey = (address: Address) =>
  [address.id, address.street, address.number, address.district, address.city, address.latitude, address.longitude].join("|")

/** Quotes every saved address with the core so the fee shows right after the address is confirmed; re-quotes on any change. */
export function useDeliveryQuotes(
  merchantId: string | undefined,
  addresses: Address[],
  scheduled: boolean,
  enabled: boolean,
): Record<string, AddressQuote> {
  const [quotes, setQuotes] = useState<Record<string, AddressQuote>>({})
  const signature = addresses.map(addressKey).join(";")
  const addressesRef = useRef(addresses)
  addressesRef.current = addresses

  useEffect(() => {
    const addresses = addressesRef.current
    if (!enabled || !merchantId || addresses.length === 0) {
      setQuotes({})
      return
    }
    let cancelled = false
    setQuotes(Object.fromEntries(addresses.map((a) => [a.id, { status: "loading" } as AddressQuote])))
    addresses.forEach((address) => {
      fetchDeliveryQuote(merchantId, address, scheduled).then((quote) => {
        if (cancelled) return
        setQuotes((prev) => ({
          ...prev,
          [address.id]: quote ? { status: "ready", quote } : { status: "unavailable" },
        }))
      })
    })
    return () => {
      cancelled = true
    }
  }, [merchantId, signature, scheduled, enabled])

  return quotes
}
